// PKCE + state OAuth helpers — runs in the browser only.

const VERIFIER_KEY = 'onlybans:oauth:code_verifier'
const STATE_KEY = 'onlybans:oauth:state'

function base64Url(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

async function sha256Base64Url(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return base64Url(new Uint8Array(buf))
}

export interface StartLoginOptions {
  clientId: string
  redirectUri: string
}

/** Stage PKCE + state, then redirect to Google. */
export async function startGoogleLogin(opts: StartLoginOptions): Promise<void> {
  const verifier = randomString(48) // ~64 base64url chars, well within 43-128 spec
  const challenge = await sha256Base64Url(verifier)
  const state = randomString(16)

  sessionStorage.setItem(VERIFIER_KEY, verifier)
  sessionStorage.setItem(STATE_KEY, state)

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth')
  url.searchParams.set('client_id', opts.clientId)
  url.searchParams.set('redirect_uri', opts.redirectUri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', 'openid email profile')
  url.searchParams.set('state', state)
  url.searchParams.set('code_challenge', challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('access_type', 'online')
  url.searchParams.set('prompt', 'select_account')

  window.location.assign(url.toString())
}

export interface CallbackParams {
  code: string
  codeVerifier: string
}

/** Validate state and return { code, codeVerifier } for the server exchange. */
export function consumeCallback(searchParams: URLSearchParams): CallbackParams {
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const error = searchParams.get('error')

  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const expectedState = sessionStorage.getItem(STATE_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)

  if (error) throw new Error(`Google returned error: ${error}`)
  if (!code) throw new Error('Missing authorization code')
  if (!state || state !== expectedState) throw new Error('OAuth state mismatch (possible CSRF)')
  if (!verifier) throw new Error('Missing PKCE verifier in sessionStorage')

  return { code, codeVerifier: verifier }
}
